import type { KitchenTicket } from '../domain/hospitalityTypes';
import {
  displayKitchenLine,
  kitchenTicketPrimaryRef,
  kitchenTicketPublicNotes,
  kitchenTicketSource,
  kitchenTicketSourceLabel,
} from '../domain/kitchenTicketPresentation';
import {
  receiptColsForBrowser,
  receiptKitchenItemBlock,
  receiptSeparatorLine,
  wrapThermalPlainDocumentHtml,
  type ReceiptWidthMm,
  type ThermalPlainLineRole,
} from '@vuelto/print';

export type KitchenTicketComandaPayload = { text: string; html: string };

function whenLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm} ${hh}:${mi}`;
}

/** Comanda cocina térmica (58–80 mm). Sin precios: solo producción. */
export function buildKitchenTicketComanda(
  ticket: KitchenTicket,
  opts?: { tableName?: string | null; widthMm?: ReceiptWidthMm },
): KitchenTicketComandaPayload {
  const widthMm = opts?.widthMm ?? 72;
  const cols = receiptColsForBrowser(widthMm);
  const sep = receiptSeparatorLine(cols);
  const code = kitchenTicketPrimaryRef(ticket);
  const source = kitchenTicketSourceLabel(kitchenTicketSource(ticket));
  const when = whenLabel(ticket.createdAt);
  const notes = kitchenTicketPublicNotes(ticket.notes);
  const comandaLabel = `Comanda ${code}`;

  const lineTexts = (ticket.lines ?? []).map((raw) => {
    const line = displayKitchenLine(raw);
    const mods = line.optionsLabel ? ` (${line.optionsLabel})` : '';
    const note = line.lineNote ? ` — ${line.lineNote}` : '';
    return `${raw.qty}× ${line.name}${mods}${note}`;
  });

  const textParts = [
    comandaLabel,
    source,
    when,
    opts?.tableName ? `Mesa: ${opts.tableName}` : '',
    notes,
    '',
    ...(lineTexts.length ? lineTexts : ['(sin líneas)']),
  ].filter((p, i, arr) => !(p === '' && arr[i - 1] === ''));

  const text = textParts.join('\n');

  const headerLines: Array<{ text: string; role: ThermalPlainLineRole }> = [
    { text: comandaLabel.slice(0, cols), role: 'code' },
    {
      text: (when ? `${source} · ${when}` : source).slice(0, cols),
      role: 'meta',
    },
  ];
  if (opts?.tableName?.trim()) {
    headerLines.push({
      text: `Mesa: ${opts.tableName.trim()}`.slice(0, cols),
      role: 'meta',
    });
  }
  if (notes?.trim()) {
    headerLines.push({ text: notes.trim().slice(0, cols), role: 'notes' });
  }
  headerLines.push({ text: sep, role: 'sep' });

  const itemLines =
    (ticket.lines ?? []).length > 0
      ? (ticket.lines ?? []).flatMap((raw) => {
          const line = displayKitchenLine(raw);
          const qtyLine = `${raw.qty}× ${line.name}`;
          const optsLine = line.optionsLabel?.trim() || null;
          const noteLine = line.lineNote?.trim() || null;
          return receiptKitchenItemBlock(qtyLine, optsLine, noteLine, cols);
        })
      : ['(sin líneas)'];

  const html = wrapThermalPlainDocumentHtml({
    widthMm,
    headerLines,
    itemsPre: itemLines.join('\n'),
    footerLines: [],
  });

  return { text, html };
}
